import type { TabCategory } from '@/utils/tabCategories';
import type { ApiBrowserTab } from '@/types';
import { activityFromTimestamp, categorizeTabUrl, hostFromTabUrl } from '@/utils/tabCategories';
import { getCachedTabSecurity } from '@/components/tabs/tabSecurity';

export type TimeRange = '1h' | '24h' | '7d' | '30d';

export interface TabRow {
  id: string;
  title: string;
  url: string;
  category: TabCategory;
  activity: number;
  /** null until the tab has been scanned (see scanTabsSecurity). */
  securityScore: number | null;
  relationships: number;
  lastAccessedAt: string;
  prediction: string;
}

export interface TabPredictionRow {
  id: string;
  tab_id: string;
  action: string;
  confidence: number;
  reason: string;
}

export const TIME_RANGE_MS: Record<TimeRange, number> = {
  '1h': 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
};

export const TIME_RANGE_LABEL: Record<TimeRange, string> = {
  '1h': 'Last hour',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

export function filterTabsByTimeRange(tabs: TabRow[], range: TimeRange): TabRow[] {
  const cutoff = Date.now() - TIME_RANGE_MS[range];
  return tabs.filter((t) => new Date(t.lastAccessedAt).getTime() >= cutoff);
}

export function formatTabTimestamp(iso: string): { date: string; time: string; full: string } | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
    time: date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    full: date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
  };
}

export function buildPredictionMap(predictions: TabPredictionRow[]): Map<string, TabPredictionRow> {
  const map = new Map<string, TabPredictionRow>();
  for (const p of predictions) {
    const existing = map.get(p.tab_id);
    if (!existing || p.confidence > existing.confidence) {
      map.set(p.tab_id, p);
    }
  }
  return map;
}

export function formatPredictionLabel(pred?: TabPredictionRow): string {
  if (!pred) return 'No action predicted';
  const action = pred.action.replace(/_/g, ' ');
  const label = action.charAt(0).toUpperCase() + action.slice(1);
  const pct = Math.min(100, Math.round(pred.confidence * 100));
  return `${label} (${pct}%)`;
}

export function transformApiTabs(apiTabs: ApiBrowserTab[], predictions: TabPredictionRow[]): TabRow[] {
  const activeTabs = apiTabs.filter((t) => !t.status || t.status === 'active');
  const tabs = activeTabs.length > 0 ? activeTabs : apiTabs;
  const predMap = buildPredictionMap(predictions);

  const hostCounts = new Map<string, number>();
  for (const t of tabs) {
    const h = hostFromTabUrl(t.url);
    if (h) hostCounts.set(h, (hostCounts.get(h) ?? 0) + 1);
  }

  return tabs.map((t) => {
    const host = hostFromTabUrl(t.url);
    const related = host ? Math.max(0, (hostCounts.get(host) ?? 1) - 1) : 0;
    const updatedAt = t.updated_at || t.created_at || '';
    const tabKey = t.id || t.tab_id;
    const pred = predMap.get(tabKey) ?? predMap.get(t.tab_id);

    return {
      id: tabKey,
      title: t.title || t.url || 'Untitled',
      url: t.url || '',
      category: categorizeTabUrl(t.url),
      activity: activityFromTimestamp(updatedAt),
      securityScore: getCachedTabSecurity(tabKey, t.url || '')?.score ?? null,
      relationships: related,
      lastAccessedAt: updatedAt || new Date().toISOString(),
      prediction: formatPredictionLabel(pred),
    };
  });
}
